import React, { useEffect, useMemo } from 'react';
import { useTable, useSortBy, useExpanded, usePagination } from 'react-table';
import {
  Card,
  Box,
  Flex,
  Button,
  Text,
  Column,
  Select,
} from '../design-system';
import styled from 'styled-components';
import {
  FaCaretDown,
  FaCaretRight,
  FaEnvelope,
  FaEdit,
  FaRegTrashAlt,
  FaStickyNote,
  FaRegStickyNote,
} from 'react-icons/fa';
import { get } from 'lodash';
import { utcToLocal } from '../helpers/utils';
import Loadable from './Loadable';
import SortIndicator, { ariaSort, HeaderContent } from './SortIndicator';

const More = styled(Flex)`
  margin-left: 1rem;
  &:hover {
    cursor: pointer;
  }
`;

const StyledTable = styled.table`
  flex: 1;
  border-spacing: 0;
  border-collapse: collapse;
  width: 100%;
  font-size: 90%;
  tbody > tr:nth-child(odd) {
    background-color: ${(p) => p.theme.colors.greyLightest};
  }
  th {
    font-size: 14px;
    position: sticky;
    top: 0;
    background-color: white;
    /* Headers that wrap to two lines otherwise sit at a different height than
       their single-line neighbours. */
    vertical-align: middle;
  }
  thead > tr > th:first-child,
  tbody > tr > td:first-child {
    padding-left: 2rem;
  }
  thead > tr > th:last-child,
  tbody > tr > td:last-child {
    padding-right: 2rem;
  }
  td,
  th {
    text-align: left;
    padding: 8px;
    max-width: 400px;
    min-width: 100px;
  }
  td {
    height: 56px;
    overflow-wrap: break-word;
  }
`;

interface CollapsibleSubRowProps {
  secondaryColumns: any[];
  actionColumns: any[];
  original: any;
}

const CollapsibleSubRowComponent = ({
  secondaryColumns = [],
  actionColumns = [],
  original = {},
}: CollapsibleSubRowProps) => {
  const safeSecondary = secondaryColumns ?? [];
  const safeActions = actionColumns ?? [];
  const { rows, prepareRow } = useTable({
    columns: [...safeSecondary, ...safeActions],
    data: [{ ...(original ?? {}) }],
  });

  const filteredColumns = safeSecondary.filter(
    (column) =>
      typeof column.accessor === 'string' && !column.accessor.includes('notes'),
  );
  if (!rows?.length) return null;
  const subRow = { ...rows[0] };
  prepareRow(subRow);

  // To generalize accessors
  const notesAccessor = get(
    safeSecondary.find(
      (column) =>
        typeof column.accessor === 'string' &&
        column.accessor.toLowerCase().includes('notes'),
    ),
    'accessor',
  );

  const editAccessor = get(
    safeActions.find(
      (column) =>
        typeof column.accessor === 'string' &&
        column.accessor.toLowerCase().includes('edit'),
    ),
    'accessor',
  );

  const addAccessor = get(
    safeActions.find(
      (column) =>
        typeof column.accessor === 'string' &&
        (column.accessor.toLowerCase().includes('add') ||
          column.accessor.toLowerCase().includes('create')),
    ),
    'accessor',
  );

  // Needed fields
  const notes = subRow.cells.find((cell) => cell.column.id === notesAccessor);
  const editAction = subRow.cells.find(
    (cell) => cell.column.id === editAccessor,
  );
  const addAction = subRow.cells.find((cell) => cell.column.id === addAccessor);

  return (
    <Card pt={4} pb={1} position="relative">
      {editAction && original?.canEdit && (
        <Box position="absolute" right={-4} top={-6}>
          {editAction.render('Cell')}
        </Box>
      )}
      {filteredColumns.length > 0 && (
        <Column>
          <Card mb={1} pt={1} pb={1}>
            <Flex>
              {filteredColumns.map((column) => {
                const details = subRow.cells.find(
                  (cell) => cell.column.id === column.accessor,
                );
                return (
                  <Column key={column.accessor}>
                    <Text fontWeight="bold" fontSize={14} mb={1}>
                      {column.Header}
                    </Text>
                    <Text fontSize={13}>
                      {details ? details.render('Cell') : null}
                    </Text>
                  </Column>
                );
              })}
            </Flex>
          </Card>
        </Column>
      )}
      {notes && <Column>{notes.render('Cell')}</Column>}
      {addAction && (
        <Column>
          <Flex justifyContent="flex-end" alignItems="center" mb={1}>
            {addAction.render('Cell')}
          </Flex>
        </Column>
      )}
    </Card>
  );
};

// Key rows by entity identity, not array position. With index keys, React
// re-uses cell instances for whichever row lands at that position after a
// refetch/sort/page change, so stateful cells (status selects) can display
// one row's state against another row's data. Rows without an `id` need a
// caller-supplied getRowId.
const defaultGetRowId = (row: any, relativeIndex: number) =>
  row.id != null ? String(row.id) : String(relativeIndex);

interface TableProps {
  name?: string;
  onPageChange?: (params: {
    pageIndex: number;
    pageSize: number;
    sortColumn?: string;
    sortDesc?: boolean;
  }) => void;
  columns: any[];
  data: any;
  loading?: boolean;
  pagination?: boolean;
  initialSortBy?: any[];
  getRowId?: (row: any, relativeIndex: number) => string;
}

export const Table = ({
  name,
  onPageChange,
  columns,
  data,
  loading,
  pagination = true,
  initialSortBy = [],
  getRowId = defaultGetRowId,
}: TableProps) => {
  // Check if data is paginated from server
  // Ensure tableData is always an array to prevent errors in react-table
  const tableData = useMemo(() => {
    if (!data) return [];
    return data.current_page
      ? data.data || []
      : Array.isArray(data)
        ? data
        : [];
  }, [data]);

  const isPaginatedByServer = !!(data && data.current_page);

  const {
    current_page: currentPage = 1,
    last_page: tablePageCount = 0,
    per_page: perPage = 30,
  } = useMemo(
    () => (isPaginatedByServer && data ? data : {}),
    [data, isPaginatedByServer],
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      manualPagination: isPaginatedByServer,
      pageCount: tablePageCount,
      initialState: {
        pageIndex: currentPage - 1,
        pageSize: perPage,
        sortBy: initialSortBy,
      },
      columns,
      data: tableData,
      getRowId,
      manualSortBy: isPaginatedByServer,
      // With a default sort in play the toggle cycle stays asc <-> desc. The
      // third "unsorted" state would leave the server's ordering in place while
      // the header claimed nothing was sorted.
      disableSortRemove: initialSortBy.length > 0,
    },
    useSortBy,
    usePagination,
  );

  const [sorting] = sortBy;

  const { id: sortColumn, desc: sortDesc } = sorting || {};

  useEffect(() => {
    if (onPageChange) {
      onPageChange({ pageIndex, pageSize, sortColumn, sortDesc });
    }
  }, [pageIndex, pageSize, sortColumn, sortDesc]);

  const rowData = pagination ? page : rows;

  return (
    <Flex
      flex={1}
      height="100%"
      minHeight="5rem"
      flexDirection="column"
      style={{ overflowX: 'scroll' }}
    >
      <Loadable loading={loading}>
        <StyledTable {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => {
              const { key: hgKey, ...hgProps } =
                headerGroup.getHeaderGroupProps();
              return (
                <tr key={hgKey} {...hgProps}>
                  {headerGroup.headers.map((column) => {
                    const { key: colKey, ...colProps } = column.getHeaderProps(
                      column.getSortByToggleProps(),
                    );
                    return (
                      <th
                        key={colKey}
                        {...colProps}
                        style={{ textAlign: column.alignRight && 'right' }}
                        aria-sort={ariaSort(column)}
                      >
                        <HeaderContent $alignRight={column.alignRight}>
                          {column.render('Header')}
                          <SortIndicator column={column} />
                        </HeaderContent>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {(rowData?.length ?? 0) > 0 ? (
              rowData.map((row) => {
                prepareRow(row);
                const { key: rowKey, ...rowProps } = row.getRowProps();
                return (
                  <tr key={rowKey} {...rowProps}>
                    {row.cells.map((cell) => {
                      const { key: cellKey, ...cellProps } =
                        cell.getCellProps();
                      return (
                        <td
                          key={cellKey}
                          {...cellProps}
                          style={{
                            textAlign: cell.column.alignRight && 'right',
                            letterSpacing: cell.column.alignRight && '0.5px',
                          }}
                        >
                          {cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  style={{ textAlign: 'center', padding: '2rem' }}
                  colSpan={columns?.length ?? 1}
                >
                  No {name || 'Data'}
                </td>
              </tr>
            )}
          </tbody>
        </StyledTable>
        {pagination && pageCount > 1 && (
          <Flex
            justifyContent="center"
            alignItems="center"
            mt={4}
            py={3}
            style={{ borderTop: '1px solid lightGrey' }}
          >
            <Box mr={3}>
              <span>
                Page{' '}
                <strong>
                  {pageIndex + 1} of {pageCount || 1}
                </strong>{' '}
              </span>
            </Box>
            <Box mr={3}>
              <Button.Group>
                <Button
                  size="sm"
                  leftIcon="page-first"
                  onClick={() => gotoPage(0)}
                  disabled={!canPreviousPage}
                />
                <Button
                  size="sm"
                  leftIcon="chevron-left"
                  onClick={() => previousPage(0)}
                  disabled={!canPreviousPage}
                />
                <Button
                  size="sm"
                  leftIcon="chevron-right"
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                />
                <Button
                  size="sm"
                  leftIcon="page-last"
                  onClick={() => gotoPage(pageCount - 1)}
                  disabled={!canNextPage}
                />
              </Button.Group>
            </Box>
            <Flex alignItems="center">
              <Box mr={2}>Show</Box>
              <Box>
                <Select
                  size="sm"
                  style={{ width: 70 }}
                  name="pageSize"
                  options={[10, 20, 30, 40, 50].map((size) => ({
                    label: size,
                    value: size,
                  }))}
                  value={pageSize.toString()}
                  onChange={(_, value) => {
                    gotoPage(0);
                    setPageSize(Number(value));
                  }}
                />
              </Box>
            </Flex>
          </Flex>
        )}
      </Loadable>
    </Flex>
  );
};

interface CollapsibleRowTableProps {
  name?: string;
  columns: {
    type?: string;
    primary?: any[];
    secondary?: any[];
    actions?: any[];
  };
  data: any[];
}

export const CollapsibleRowTable = ({
  name,
  columns: { type = 'default', primary = [], secondary = [], actions = [] },
  data = [],
}: CollapsibleRowTableProps) => {
  // react-table v7's auto-reset effects dispatch whenever the columns array
  // identity changes; an inline array rebuilt on every render makes each
  // dispatch trigger the next one — an infinite update loop.
  const safeData = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const columns = useMemo(
    () => [
      ...primary,
      {
        Header: '',
        id: 'expander',
        Cell: ({ row }: { row: any }) => {
          return (
            <Flex justifyContent="space-around" alignItems="center">
              {row.original?.canEdit && (
                <Button.Group>
                  <Button
                    id="edit-icon-button"
                    variant="primaryText"
                    onClick={() => row.original.openEditModal(row.original)}
                  >
                    <FaEdit fontSize={20} />
                  </Button>
                  {name === 'Actions' && (
                    <Button
                      variant="primaryText"
                      onClick={() =>
                        row.original.openNotifyAction(row.original)
                      }
                    >
                      <FaEnvelope fontSize={20} />
                    </Button>
                  )}
                  <Button
                    id="delete-icon-button"
                    variant="primaryText"
                    onClick={() =>
                      row.original.handleDeleteAction(row.original.id)
                    }
                  >
                    <FaRegTrashAlt fontSize={20} />
                  </Button>
                  <More
                    id="expand-button"
                    justifyContent="flex-end"
                    {...(typeof row.getExpandedToggleProps === 'function'
                      ? row.getExpandedToggleProps()
                      : {})}
                  >
                    {row.isExpanded ? (
                      <Button variant="primaryText">
                        <FaStickyNote fontSize={20} />
                      </Button>
                    ) : (
                      <Button variant="primaryText">
                        <FaRegStickyNote fontSize={20} />
                      </Button>
                    )}
                  </More>
                </Button.Group>
              )}
            </Flex>
          );
        },
      },
    ],
    [primary, name],
  );

  const table = useTable(
    {
      columns,
      data: safeData,
    },
    useSortBy,
    useExpanded,
  );
  const { getTableProps, getTableBodyProps, headerGroups, prepareRow } =
    table as any;
  const rows = (table as any).rows ?? [];
  const flatColumns = (table as any).flatColumns ?? [];
  return (
    <StyledTable {...getTableProps()}>
      <thead>
        {headerGroups.map((headerGroup) => {
          const { key: hgKey, ...hgProps } = headerGroup.getHeaderGroupProps();
          return (
            <tr key={hgKey} {...hgProps}>
              {headerGroup.headers.map((column) => {
                const { key: colKey, ...colProps } = column.getHeaderProps(
                  column.getSortByToggleProps(),
                );
                return (
                  <th key={colKey} {...colProps} aria-sort={ariaSort(column)}>
                    <HeaderContent $alignRight={column.alignRight}>
                      {column.render('Header')}
                      <SortIndicator column={column} />
                    </HeaderContent>
                  </th>
                );
              })}
            </tr>
          );
        })}
      </thead>
      <tbody {...getTableBodyProps()}>
        {(rows?.length ?? 0) > 0 ? (
          rows.map((row) => {
            prepareRow(row);
            const { key: rowKey } = row.getRowProps();
            return (
              <React.Fragment key={rowKey}>
                <tr>
                  {row.cells.map((cell) => {
                    const { key: cellKey, ...cellProps } = cell.getCellProps();
                    return (
                      <td key={cellKey} {...cellProps}>
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
                {row.isExpanded && (
                  <tr>
                    <td colSpan={flatColumns?.length ?? 1}>
                      <CollapsibleSubRowComponent
                        secondaryColumns={secondary}
                        actionColumns={actions}
                        original={row.original}
                      />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })
        ) : (
          <tr>
            <td
              style={{ textAlign: 'center', padding: '2rem' }}
              colSpan={flatColumns?.length ?? 1}
            >
              No {name || 'Data'}
            </td>
          </tr>
        )}
      </tbody>
    </StyledTable>
  );
};

interface TableCardHeaderProps {
  name?: string;
  action?: string;
  onClick?: () => void;
  disabled?: boolean;
  updatedAt?: string;
}

export const TableCardHeader = ({
  name,
  action,
  onClick,
  disabled,
  updatedAt,
}: TableCardHeaderProps) => (
  <Card.Header>
    <Flex justifyContent="flex-start" alignItems="center">
      <Text fontWeight={700} fontSize={20} lineHeight="20px" mr={3}>
        {name}
      </Text>
      {updatedAt && (
        <Text
          fontWeight={500}
          fontSize={14}
          lineHeight="14px"
          color="grey"
          alignSelf="flex-end"
        >
          Updated: {utcToLocal(updatedAt).fromNow()}
        </Text>
      )}
      {!disabled && (
        <Box justifySelf="flex-end" ml="auto">
          <Button
            id="table-header-button-1"
            variant="primaryText"
            type="button"
            onClick={onClick}
            leftIcon="plus"
            leftIconProps={{ size: 20 }}
          >
            {action}
          </Button>
        </Box>
      )}
    </Flex>
  </Card.Header>
);

export const TableBody = styled(Card.Body)`
  overflow-x: auto;
  padding: 0;
`;
